/**
 * Newsletter Popup - DSGVO Compliant
 * Extends modal-dialog with Klaviyo integration
 * Features: Hybrid trigger (time + scroll), focus management, localStorage persistence
 */

if (!customElements.get('newsletter-popup')) {
  class NewsletterPopup extends HTMLElement {
    constructor() {
      super();

      // Configuration
      this.id = 'NewsletterPopup';
      this.delay = parseInt(this.dataset.delay, 10) * 1000 || 45000; // Default 45s
      this.scrollThreshold = 0.7; // 70% scroll depth
      this.autoCloseDelay = 3000; // 3 seconds after success

      // Klaviyo API configuration
      this.klaviyoConfig = {
        companyId: 'Y9pvmG', // Public API key
        listId: 'SANaye',
        endpoint: 'https://a.klaviyo.com/client/subscriptions/'
      };

      // State
      this.hasShown = false;
      this.isSubmitting = false;
      this.previouslyFocused = null;
      this.scrollTriggered = false;
      this.timeTriggered = false;

      // DOM elements
      this.dialog = this.querySelector('[role="dialog"]');
      this.closeButton = this.querySelector('#ModalClose-NewsletterPopup');
      this.declineButton = this.querySelector('.newsletter-popup__decline');
      this.form = this.querySelector('.newsletter-popup__form');
      this.emailInput = this.querySelector('#newsletter-popup-email');
      this.submitButton = this.querySelector('.newsletter-popup__submit');
      this.consentCheckbox = this.querySelector('#newsletter-popup-consent');
      this.errorContainer = this.querySelector('#newsletter-popup-error');
      this.errorMessage = this.errorContainer ? this.errorContainer.querySelector('.error-message') : null;
      this.successContainer = this.querySelector('.newsletter-popup__success');

      // Focusable elements for focus trap
      this.focusableSelectors = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

      this.init();
    }

    init() {
      // Check if should show popup
      if (!this.shouldShowPopup()) {
        return;
      }

      // Set up event listeners
      this.bindEvents();

      // Set up triggers
      this.setupTriggers();

      // Support Shopify design mode
      if (window.Shopify && window.Shopify.designMode) {
        this.show();
      }
    }

    shouldShowPopup() {
      // Check sessionStorage - only show once per session
      if (sessionStorage.getItem('newsletter_popup_shown_session')) {
        return false;
      }

      // Check if already subscribed (localStorage)
      if (localStorage.getItem('newsletter_popup_subscribed') === 'true') {
        return false;
      }

      // Check if dismissed recently (7 days)
      const dismissed = localStorage.getItem('newsletter_popup_dismissed');
      if (dismissed) {
        const dismissedDate = new Date(dismissed);
        const daysSince = (Date.now() - dismissedDate) / (1000 * 60 * 60 * 24);
        if (daysSince < 7) {
          return false;
        }
      }

      return true;
    }

    bindEvents() {
      // Close button
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.handleDismiss());
      }

      // Decline button
      if (this.declineButton) {
        this.declineButton.addEventListener('click', () => this.handleDismiss());
      }

      // ESC key
      this.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') {
          this.handleDismiss();
        }
      });

      // Backdrop click
      this.addEventListener('click', (event) => {
        if (event.target === this) {
          this.handleDismiss();
        }
      });

      // Form submission
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.handleSubmit();
        });
      }

      // Real-time email validation
      if (this.emailInput) {
        this.emailInput.addEventListener('input', () => {
          this.clearError();
          if (this.emailInput.classList.contains('invalid')) {
            this.emailInput.classList.remove('invalid');
          }
        });
      }

      // Tab key for focus trap
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Tab') {
          this.handleTabKey(event);
        }
      });
    }

    setupTriggers() {
      // Time-based trigger
      this.timeoutId = setTimeout(() => {
        if (!this.timeTriggered && !this.hasShown) {
          this.timeTriggered = true;
          this.show('time_delay');
        }
      }, this.delay);

      // Scroll-based trigger
      const scrollHandler = () => {
        if (this.scrollTriggered || this.hasShown) {
          return;
        }

        const scrollPercentage = this.getScrollPercentage();
        if (scrollPercentage >= this.scrollThreshold) {
          this.scrollTriggered = true;
          this.show('scroll_depth');
          window.removeEventListener('scroll', scrollHandler);
        }
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });

      // Clean up on page unload
      window.addEventListener('beforeunload', () => {
        clearTimeout(this.timeoutId);
        window.removeEventListener('scroll', scrollHandler);
      });
    }

    getScrollPercentage() {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const trackLength = documentHeight - windowHeight;
      return trackLength > 0 ? scrollTop / trackLength : 0;
    }

    show(trigger = 'manual') {
      if (this.hasShown) return;

      this.hasShown = true;
      this.trigger = trigger;

      // Mark as shown in session
      sessionStorage.setItem('newsletter_popup_shown_session', 'true');

      // Store previously focused element
      this.previouslyFocused = document.activeElement;

      // Show modal
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');

      // Announce to screen readers
      this.announceToScreenReader('Newsletter-Popup geöffnet. Erhalte 10% Rabatt auf deine erste Bestellung.');

      // Focus email input after animation
      setTimeout(() => {
        if (this.emailInput) {
          this.emailInput.focus();
        }
      }, 100);
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');

      // Return focus to previously focused element
      if (this.previouslyFocused && this.previouslyFocused.focus) {
        this.previouslyFocused.focus();
      }

      // Clear timeout if still pending
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
    }

    handleDismiss() {
      // Store dismissal timestamp
      localStorage.setItem('newsletter_popup_dismissed', new Date().toISOString());

      this.announceToScreenReader('Newsletter-Popup geschlossen.');
      this.hide();
    }

    async handleSubmit() {
      if (this.isSubmitting) return;

      const email = this.emailInput.value.trim();

      // Validate email
      if (!this.validateEmail(email)) {
        this.showError('Bitte gib eine gültige E-Mail-Adresse ein.');
        this.emailInput.classList.add('invalid');
        this.emailInput.setAttribute('aria-invalid', 'true');
        this.emailInput.focus();
        return;
      }

      // Validate consent checkbox (if present)
      if (this.consentCheckbox && !this.consentCheckbox.checked) {
        this.showError('Bitte bestätige deine Einwilligung.');
        this.consentCheckbox.focus();
        return;
      }

      this.isSubmitting = true;
      this.setLoadingState(true);

      try {
        await this.subscribeToKlaviyo(email);
        this.handleSuccess();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isSubmitting = false;
        this.setLoadingState(false);
      }
    }

    validateEmail(email) {
      const pattern = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
      return pattern.test(email);
    }

    async subscribeToKlaviyo(email) {
      // Official Klaviyo client-side subscriptions API payload (2024-10-15 revision)
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: {
                    popup_source: this.trigger || 'manual'
                  }
                },
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED'
                    }
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: this.klaviyoConfig.listId
              }
            }
          }
        }
      };

      const url = `${this.klaviyoConfig.endpoint}?company_id=${this.klaviyoConfig.companyId}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 400) {
          throw new Error('INVALID_EMAIL');
        } else if (response.status === 409 || errorData.detail?.includes('already')) {
          throw new Error('ALREADY_SUBSCRIBED');
        } else {
          throw new Error('SERVER_ERROR');
        }
      }

      // 202 Accepted returns empty body, 200 returns JSON
      if (response.status === 202) {
        return { success: true };
      }

      return response.json();
    }

    handleSuccess() {
      // Mark as subscribed (never show again)
      localStorage.setItem('newsletter_popup_subscribed', 'true');
      localStorage.removeItem('newsletter_popup_dismissed');

      // Hide form, show success message
      this.form.classList.add('fade-out');

      setTimeout(() => {
        this.form.classList.add('hidden');
        this.successContainer.classList.remove('hidden');
        this.successContainer.classList.add('fade-in');

        this.announceToScreenReader('Erfolgreich! Bitte bestätige deine E-Mail-Adresse.');

        // Auto-close after delay
        setTimeout(() => {
          this.hide();
        }, this.autoCloseDelay);
      }, 300);
    }

    handleError(error) {
      let errorMessage;

      switch (error.message) {
        case 'INVALID_EMAIL':
          errorMessage = 'Bitte gib eine gültige E-Mail-Adresse ein.';
          break;
        case 'ALREADY_SUBSCRIBED':
          errorMessage = 'Diese E-Mail-Adresse ist bereits registriert.';
          // Still mark as subscribed to avoid showing popup again
          localStorage.setItem('newsletter_popup_subscribed', 'true');
          break;
        case 'SERVER_ERROR':
          errorMessage = 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
          break;
        default:
          errorMessage = 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
      }

      this.showError(errorMessage);
      this.emailInput.classList.add('invalid');
      this.emailInput.setAttribute('aria-invalid', 'true');
      this.emailInput.focus();
    }

    showError(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message;
      }
      if (this.errorContainer) {
        this.errorContainer.classList.remove('hidden');
      }
      this.announceToScreenReader(`Fehler: ${message}`);
    }

    clearError() {
      if (this.errorContainer) {
        this.errorContainer.classList.add('hidden');
      }
      if (this.emailInput) {
        this.emailInput.classList.remove('invalid');
        this.emailInput.setAttribute('aria-invalid', 'false');
      }
    }

    setLoadingState(isLoading) {
      if (!this.submitButton) return;

      if (isLoading) {
        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading-overlay')?.classList.remove('hidden');
        this.announceToScreenReader('Anmeldung wird verarbeitet...');
      } else {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('loading');
        this.submitButton.querySelector('.loading-overlay')?.classList.add('hidden');
      }
    }

    handleTabKey(event) {
      const focusableElements = this.querySelectorAll(this.focusableSelectors);
      const focusableArray = Array.from(focusableElements);

      if (focusableArray.length === 0) return;

      const firstElement = focusableArray[0];
      const lastElement = focusableArray[focusableArray.length - 1];

      // If shift+tab on first element, focus last element
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
      // If tab on last element, focus first element
      else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    announceToScreenReader(message) {
      // Create a visually hidden live region announcement
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'visually-hidden';
      announcement.textContent = message;

      document.body.appendChild(announcement);

      // Remove after announcement
      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    }

    connectedCallback() {
      // Move to body (following modal-dialog pattern)
      if (!this.moved) {
        this.moved = true;
        document.body.appendChild(this);
      }
    }
  }

  customElements.define('newsletter-popup', NewsletterPopup);
}
